// Cookie-consent state helpers (client-side) for Alfa SouthIn Cafe.
// Stores the visitor's choice in localStorage and drives Google Consent Mode v2
// + Google Tag Manager so that analytics/advertising tags only fire for the
// categories the visitor allowed (GDPR / CCPA friendly).
//
// HOW THE PIECES FIT TOGETHER
// ---------------------------
// 1. public/index.html sets Consent Mode DEFAULTS to "denied" as the very first
//    script on the page (before anything can drop a cookie).
// 2. The visitor picks categories in src/components/CookieConsent.js.
// 3. saveConsent() persists the choice and calls applyConsent(), which pushes a
//    Consent Mode `update` + an `alfa_consent_update` dataLayer event.
// 4. loadGTM() injects GTM only once at least one non-essential category is on.
// 5. Inside the GTM dashboard you add GA4 (analytics), Meta Pixel + remarketing
//    (marketing) tags. Because Consent Mode is wired here, those tags self-gate:
//      - analytics_storage  <- Analytics category
//      - ad_storage / ad_user_data / ad_personalization <- Marketing category
//      - personalization / functionality_storage <- Personalization + Forms
//    So you never have to hand-gate each tag — set the tag's consent settings in
//    GTM and this file supplies the signals.

export const CONSENT_KEY = "alfa_cookie_consent";
export const CONSENT_VERSION = 1;

// ---- Tag / measurement IDs -------------------------------------------------
// Fill these in once the accounts exist. While they are blank the trackers
// simply never load, so the banner works safely before analytics is set up.
export const GTM_ID = ""; // Google Tag Manager container, e.g. "GTM-XXXXXXX"
export const GA4_ID = ""; // Google Analytics 4 Measurement ID, e.g. "G-XXXXXXXXXX"
export const META_PIXEL_ID = ""; // Meta (Facebook) Pixel ID (added as a tag inside GTM)

const FORM_PREFILL_KEY = "alfa_form_prefill";

// ---------------------------------------------------------------------------
// dataLayer / Google Consent Mode plumbing
// ---------------------------------------------------------------------------
function dataLayer() {
  window.dataLayer = window.dataLayer || [];
  return window.dataLayer;
}

// Canonical gtag shim (matches Google's snippet so the Consent API recognises it).
function gtag() {
  dataLayer().push(arguments);
}

// Translate our category choices into Consent Mode v2 signals and announce the
// change on the dataLayer so GTM triggers can react to it.
export function applyConsent(consent) {
  if (typeof window === "undefined" || !consent) return;
  const marketing = !!consent.marketing;
  gtag("consent", "update", {
    analytics_storage: consent.analytics ? "granted" : "denied",
    ad_storage: marketing ? "granted" : "denied",
    ad_user_data: marketing ? "granted" : "denied",
    ad_personalization: marketing ? "granted" : "denied",
    functionality_storage:
      consent.necessary || consent.forms ? "granted" : "denied",
    personalization_storage: consent.forms ? "granted" : "denied",
  });
  dataLayer().push({
    event: "alfa_consent_update",
    alfa_consent: {
      necessary: true,
      analytics: !!consent.analytics,
      marketing,
      forms: !!consent.forms,
    },
  });
}

// ---------------------------------------------------------------------------
// Stored consent
// ---------------------------------------------------------------------------

// Read the stored consent, or null if the visitor hasn't chosen yet (or the
// stored version is stale — a version bump re-prompts everyone).
export function getConsent() {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    const c = JSON.parse(raw);
    if (!c || c.v !== CONSENT_VERSION) return null;
    return c;
  } catch {
    return null;
  }
}

// Persist a choice. `necessary` is always true (strictly required cookies).
export function saveConsent({ analytics, forms, marketing }) {
  const consent = {
    necessary: true,
    analytics: !!analytics,
    forms: !!forms,
    marketing: !!marketing,
    v: CONSENT_VERSION,
    ts: Date.now(),
  };
  try {
    window.localStorage.setItem(CONSENT_KEY, JSON.stringify(consent));
  } catch {
    /* ignore quota / privacy-mode errors */
  }
  // If the visitor turned Forms off, drop any convenience data we were keeping.
  if (!consent.forms) clearFormPrefill();
  // Reflect the choice into Consent Mode + dataLayer immediately.
  applyConsent(consent);
  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent("alfa-consent-change", { detail: consent })
    );
  }
  return consent;
}

// True if the visitor has consented to a given category
// ("analytics" | "marketing" | "forms"). Strictly-necessary is always allowed.
export function hasConsent(category) {
  if (category === "necessary") return true;
  const c = getConsent();
  return c ? !!c[category] : false;
}

// True when GTM is worth loading — i.e. any tag-driven category is on.
export function needsTagManager(consent) {
  const c = consent || getConsent();
  return !!(c && (c.analytics || c.marketing));
}

// ---------------------------------------------------------------------------
// Functional "Simplified Forms" cookies (first-party convenience data)
// ---------------------------------------------------------------------------

// Remember basic contact details to pre-fill forms next time — ONLY if the
// visitor allowed the Forms category. Safe no-op otherwise.
export function rememberFormContact(data) {
  if (typeof window === "undefined" || !hasConsent("forms")) return;
  try {
    window.localStorage.setItem(FORM_PREFILL_KEY, JSON.stringify(data || {}));
  } catch {
    /* ignore */
  }
}

// Read remembered contact details (null if none or Forms consent is off).
export function getFormPrefill() {
  if (typeof window === "undefined" || !hasConsent("forms")) return null;
  try {
    return JSON.parse(window.localStorage.getItem(FORM_PREFILL_KEY) || "null");
  } catch {
    return null;
  }
}

export function clearFormPrefill() {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(FORM_PREFILL_KEY);
  } catch {
    /* ignore */
  }
}

// ---------------------------------------------------------------------------
// Google Analytics 4 + Google Tag Manager loaders
// ---------------------------------------------------------------------------

// Inject Google Analytics 4 (gtag.js) exactly once — call only after Analytics
// consent. Consent Mode already granted analytics_storage via applyConsent, so
// GA4 measures with cookies; without consent it never loads.
export function loadGA4() {
  if (typeof window === "undefined" || window.__ga4Loaded) return;
  if (!GA4_ID || GA4_ID.indexOf("G-XXXX") === 0) return; // not configured yet
  window.__ga4Loaded = true;
  const s = document.createElement("script");
  s.async = true;
  s.src = "https://www.googletagmanager.com/gtag/js?id=" + GA4_ID;
  document.head.appendChild(s);
  gtag("js", new Date());
  gtag("config", GA4_ID);
}

// Inject Google Tag Manager exactly once (only call once a tag-driven category
// is consented). Consent Mode defaults are already set in index.html, so any tag
// that loads here still respects the visitor's choices.
export function loadGTM() {
  if (typeof window === "undefined" || window.__gtmLoaded) return;
  if (!GTM_ID) return; // not configured yet
  window.__gtmLoaded = true;
  (function (w, d, s, l, i) {
    w[l] = w[l] || [];
    w[l].push({ "gtm.start": Date.now(), event: "gtm.js" });
    const f = d.getElementsByTagName(s)[0];
    const j = d.createElement(s);
    const dl = l !== "dataLayer" ? "&l=" + l : "";
    j.async = true;
    j.src = "https://www.googletagmanager.com/gtm.js?id=" + i + dl;
    f.parentNode.insertBefore(j, f);
  })(window, document, "script", "dataLayer", GTM_ID);
}
